import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, type Kayttaja, type Kohde, type Linja, type Kalusto } from "./api";
import { useKohdeSynkka } from "./Kohdekonteksti";
import { TulostePohja } from "./Tuloste";
import { TulosteKieli } from "./TulosteKieli";
import { naytaPvm } from "./pvm";
import { t, kaannos, virheTeksti, kieli as kayttoliittymanKieli } from "./kieli";
import type { KaannosAvain } from "./kaannokset";

/* ═══════════════════════════════════════════════════════════
   Koeponnistukset: koepiirit ja painekoepöytäkirja (loppudokumentaation vaihe 1; T659 + T441, Toni 3.11.2026 klo 10.30 ja
   4.11.2026 klo 07.20). Piiri = kohteen 1…n linjaa (276); piirin koe kirjataan jokaiselle linjalle (pöytäkirja per linja).
   Pöytäkirja on ytimen asiakirja (277): urakoitsija kuittaa sähköisesti, tilaaja ja tarkastuslaitos paperilla (skannattu PDF).
   Tuloste on tulosteen kielellä (209: valinta → kohteen dokumentaatiokieli), ei käyttöliittymän. Puuttuva tieto on sana, ei tyhjä.
   ═══════════════════════════════════════════════════════════ */

type Piiri = { id: number; tunnus: string; kuvaus: string | null; korvaa_id: number | null; korvattu: boolean; linjoja: number; linjat: string[];
  koepaiva: string | null; tulos: string | null; ped_luokka: string | null; ped_tuntemattomia: number; poytakirja_tila: string | null };
type Koe = { paiva: string | null; tulos: string | null; koetapa: string | null; vaaliaine: string | null; koepaine_bar: number | null;
  koepaine_lahde: string | null; pitoaika_min: number | null; alkoi: string | null; paattyi: string | null; lampotila_c: number | null;
  paine_alussa_bar: number | null; paine_lopussa_bar: number | null; mittalaite_id: number | null; mittalaite: string | null;
  mittalaite_sarjanumero: string | null; kalibrointi_voimassa_asti: string | null; kalibrointi_voimassa: boolean | null;
  riskinarvio_viite: string | null; suojaetaisyys_m: number | null; vaaravyohyke_kuitattu: boolean | null; kloridi_mg_l: number | null;
  valvoja: string | null; tarkastuslaitos: string | null; raportti: string | null; huomiot: string | null; ndt_tila_kirjattaessa: string | null;
  ndt_ohitus_peruste: string | null; rivit: number };
type Allekirjoitus = { id: number; rooli: string; tapa: "sahkoinen" | "paperi"; nimi: string; liite_id: number | null; hetki: string };
type Poytakirja = {
  piiri: { id: number; tunnus: string; kuvaus: string | null; korvaa_tunnus: string | null; kohde_tunnus: string; kohde_nimi: string;
    ped_luokka: string | null; ped_tuntemattomia: number; tarkastuslaitos_vaaditaan: boolean | null; ped_raja: string | null };
  linjat: { id: number; tunnus: string; dn_nimellinen: number | null; ped_luokka: string | null; media: string | null; putkiluokka: string | null;
    suunnittelupaine_bar: number | null; suunnittelulampotila_c: number | null; kemikaaliputkisto: boolean | null }[];
  koe: Koe | null; tiedostonimet: string[]; vaaditut: string[]; havainnot: string[]; asiakirja_id: number | null;
  allekirjoitukset: Allekirjoitus[]; tila: { tila: string; vaaditut: string[]; puuttuvat: string[] }; jaadytetty: boolean;
  kieli: { kieli: "fi" | "en"; lahde: string };
};

const ROOLIT = ["urakoitsija", "tilaaja", "tarkastuslaitos"] as const;

/* Kokeen ajat Suomen aikana riippumatta selaimen aikavyöhykkeestä (Toni 4.11.2026 klo 10.10 kohta 3: kuvausselain UTC:ssä näytti
   lomakkeella «07:00» ja tulosteessa «klo 9.00»). Lomakkeen datetime-local on Helsingin paikallisaika, ja tallennus muuntaa sen hetkeksi. */
const TZ = "Europe/Helsinki";
function helsinginOsat(d: Date): Record<string, string> {
  const o: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat("sv-SE", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit",
    minute: "2-digit", hour12: false }).formatToParts(d)) o[p.type] = p.value;
  if (o.hour === "24") o.hour = "00";
  return o;
}
/** Hetki → lomakkeen arvo `VVVV-KK-PPThh:mm` Helsingin ajassa. */
export function lomakkeelle(iso: string): string {
  const o = helsinginOsat(new Date(iso));
  return `${o.year}-${o.month}-${o.day}T${o.hour}:${o.minute}`;
}
/** Lomakkeen Helsingin paikallisaika → hetki (ISO, UTC). Kesäajan siirtymä: siirtymä lasketaan kyseiselle hetkelle. */
export function lomakkeelta(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  if (!m) return new Date(s).toISOString();
  const arvaus = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!);
  const siirtyma = (t: number) => { const o = helsinginOsat(new Date(t)); return Date.UTC(+o.year!, +o.month! - 1, +o.day!, +o.hour!, +o.minute!) - t; };
  let t = arvaus - siirtyma(arvaus);
  t = arvaus - siirtyma(t);
  return new Date(t).toISOString();
}
/** Hetki näytettäväksi Helsingin aikana: fi `2.11.2026 klo 9.00`, en `02/11/2026, 09:00`. */
export function helsinginAika(iso: string | null, kieli: "fi" | "en", klo = "klo"): string | null {
  if (!iso) return null;
  const o = helsinginOsat(new Date(iso));
  return kieli === "en" ? `${o.day}/${o.month}/${o.year}, ${o.hour}:${o.minute}`
    : `${+o.day!}.${+o.month!}.${o.year} ${klo} ${+o.hour!}.${o.minute}`;
}
const KIRJAAJAT = ["paakayttaja", "suunnittelija", "tyonjohtaja", "tarkastaja"];
const KUITTAAJAT = ["paakayttaja", "suunnittelija", "tyonjohtaja"];
const onRooli = (k: Kayttaja, r: string[]) => k.roolit.some((x) => r.includes(x));
const virhe = (e: unknown) => e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e);
const luku = (s: string) => (s.trim() === "" ? null : Number(s.replace(",", ".")));

export default function Koeponnistukset({ kayttaja }: { kayttaja: Kayttaja }) {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohde, setKohde] = useState<number | "">("");
  const { ylapalkissa } = useKohdeSynkka(kohde, setKohde, { kaikkiSallittu: false });
  const [piirit, setPiirit] = useState<Piiri[] | null>(null);
  const [auki, setAuki] = useState<number | null>(null);
  const [tuloste, setTuloste] = useState<number | null>(null);
  const [uusi, setUusi] = useState(false);
  const [viesti, setViesti] = useState<string | null>(null);
  const [kerta, setKerta] = useState(0);

  useEffect(() => { void api.kohteet().then((k) => { setKohteet(k); setKohde((v) => v === "" && k.length ? (k.find((x) => (x.linjoja ?? 0) > 0) ?? k[0]!).id : v); }); }, []);
  useEffect(() => {
    if (kohde === "") return;
    setPiirit(null);
    api.koepiirit(kohde).then((r) => setPiirit(r.piirit as Piiri[])).catch((e) => setViesti(virhe(e)));
  }, [kohde, kerta]);
  const paivita = () => setKerta((x) => x + 1);

  return (
    <section className="panel" data-koeponnistukset="">
      <h2>{t("kpk.valikko")}</h2>
      {!ylapalkissa && (
        <label>{t("kpk.kohde")}{" "}
          <select value={kohde} onChange={(e) => setKohde(e.target.value === "" ? "" : Number(e.target.value))}>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} {k.nimi}</option>)}
          </select>
        </label>)}
      {viesti && <p className="huom huom--vika" role="alert">{viesti}</p>}
      {onRooli(kayttaja, KIRJAAJAT) && kohde !== "" && (
        uusi ? <UusiPiiri kohdeId={kohde} valmis={(id) => { setUusi(false); if (id) { setAuki(id); paivita(); } }} />
             : <p><button className="nappi" data-koepiiri-uusi="" onClick={() => setUusi(true)}>{t("kpk.uusi_piiri")}</button></p>)}
      {piirit === null ? <p className="dim">{t("kpk.ladataan")}</p>
        : piirit.length === 0 ? <p className="dim" data-rivit="0" data-joukko="koepiirit">{t("kpk.tyhja")}</p>
        : (
          <table className="taulu" data-rivit={piirit.length} data-joukko="koepiirit">
            <thead><tr><th>{t("kpk.piiri")}</th><th>{t("kpk.linjat")}</th><th>{t("kpk.ped")}</th><th>{t("kpk.paiva")}</th>
              <th>{t("kpk.tulos")}</th><th>{t("kpk.poytakirja")}</th><th /></tr></thead>
            <tbody>
              {piirit.map((p) => (
                <tr key={p.id} data-koepiiri={p.tunnus}>
                  <td>{p.tunnus}{p.korvattu && <span className="dim"> · {t("kpk.korvattu")}</span>}</td>
                  <td title={p.linjat.join(", ")}>{t("kpk.linjoja", { n: p.linjoja })}</td>
                  <td>{p.ped_luokka ?? (p.ped_tuntemattomia ? t("kpk.ped_avoin_lyhyt") : "–")}</td>
                  <td>{p.koepaiva ? naytaPvm(p.koepaiva) : "–"}</td>
                  <td>{p.tulos ? t(`kpk.tulos_${p.tulos}` as KaannosAvain) : t("kpk.ei_koetta")}</td>
                  <td data-poytakirja-tila={p.poytakirja_tila ?? "ei"}>{p.poytakirja_tila === "allekirjoitettu" ? t("kpk.tila_valmis")
                    : p.poytakirja_tila === "luonnos" ? t("kpk.tila_odottaa_lyhyt") : "–"}</td>
                  <td>
                    <button className="nappi nappi--kevyt" data-koepiiri-avaa={p.tunnus} onClick={() => setAuki(auki === p.id ? null : p.id)}>
                      {auki === p.id ? t("kpk.sulje") : t("kpk.avaa")}</button>{" "}
                    <button className="nappi nappi--kevyt" data-koepiiri-tuloste={p.tunnus} onClick={() => setTuloste(p.id)}>{t("kpk.poytakirja")}</button>
                  </td>
                </tr>))}
            </tbody>
          </table>)}
      {auki !== null && <PiirinKoe key={auki} piiriId={auki} kayttaja={kayttaja} muuttui={paivita} poistettu={() => { setAuki(null); paivita(); }} />}
      {tuloste !== null && <Poytakirjatuloste piiriId={tuloste} sulje={() => setTuloste(null)} />}
    </section>
  );
}

function UusiPiiri({ kohdeId, valmis }: { kohdeId: number; valmis: (id: number | null) => void }) {
  const [linjat, setLinjat] = useState<Linja[]>([]);
  const [haku, setHaku] = useState("");
  const [valitut, setValitut] = useState<Set<number>>(new Set());
  const [tunnus, setTunnus] = useState("");
  const [kuvaus, setKuvaus] = useState("");
  const [virheTila, setVirhe] = useState<string | null>(null);
  useEffect(() => { void api.linjat(undefined, kohdeId, 2000).then(setLinjat); }, [kohdeId]);
  const nakyvat = useMemo(() => linjat.filter((l) => !haku || l.tunnus.toLowerCase().includes(haku.toLowerCase())).slice(0, 200), [linjat, haku]);
  const valitutLinjat = linjat.filter((l) => valitut.has(l.id));
  const mediat = [...new Set(valitutLinjat.map((l) => l.media ?? "").filter(Boolean))];
  const vaihda = (id: number) => setValitut((v) => { const n = new Set(v); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const luo = async () => {
    try { const r = await api.luoKoepiiri({ kohde_id: kohdeId, tunnus, kuvaus: kuvaus || undefined, linja_idt: [...valitut] }); valmis(r.id); }
    catch (e) { setVirhe(virhe(e)); }
  };
  return (
    <div className="panel" data-koepiiri-lomake="">
      <h3>{t("kpk.uusi_piiri")}</h3>
      <label>{t("kpk.tunnus")} <input value={tunnus} onChange={(e) => setTunnus(e.target.value)} data-koepiiri-tunnus="" /></label>{" "}
      <label>{t("kpk.kuvaus")} <input value={kuvaus} onChange={(e) => setKuvaus(e.target.value)} /></label>
      <p className="dim">{t("kpk.ehdotus")}</p>
      {mediat.length > 1 && <p className="huom huom--varoitus" data-koepiiri-havainto="">{t("kpk.eri_media", { mediat: mediat.join(", ") })}</p>}
      <label>{t("kpk.haku")} <input value={haku} onChange={(e) => setHaku(e.target.value)} data-koepiiri-haku="" /></label>
      <div style={{ maxHeight: 260, overflow: "auto" }}>
        {nakyvat.map((l) => (
          <label key={l.id} style={{ display: "block" }}>
            <input type="checkbox" checked={valitut.has(l.id)} onChange={() => vaihda(l.id)} data-koepiiri-linja={l.tunnus} /> {l.tunnus}
            <span className="dim"> {l.media ?? ""}{l.dn_nimellinen != null ? ` · DN ${l.dn_nimellinen}` : ""}</span>
          </label>))}
      </div>
      <p>{t("kpk.valittu", { n: valitut.size })}</p>
      {virheTila && <p className="huom huom--vika" role="alert">{virheTila}</p>}
      <button className="nappi nappi--paa" disabled={!tunnus.trim() || valitut.size === 0} onClick={() => void luo()} data-koepiiri-luo="">{t("kpk.luo")}</button>{" "}
      <button className="nappi nappi--kevyt" onClick={() => valmis(null)}>{t("kpk.peru")}</button>
    </div>
  );
}

type Lomake = Record<string, string>;
const LOMAKE_KENTAT = ["paiva", "tulos", "koetapa", "vaaliaine", "koepaine_bar", "koepaine_lahde", "pitoaika_min", "alkoi", "paattyi", "lampotila_c",
  "paine_alussa_bar", "paine_lopussa_bar", "mittalaite_id", "riskinarvio_viite", "suojaetaisyys_m", "kloridi_mg_l", "valvoja", "tarkastuslaitos",
  "raportti", "huomiot", "ndt_ohitus_peruste"] as const;
const NUMEROT = new Set(["koepaine_bar", "pitoaika_min", "lampotila_c", "paine_alussa_bar", "paine_lopussa_bar", "suojaetaisyys_m", "kloridi_mg_l", "mittalaite_id"]);

function PiirinKoe({ piiriId, kayttaja, muuttui, poistettu }: { piiriId: number; kayttaja: Kayttaja; muuttui: () => void; poistettu: () => void }) {
  const [pk, setPk] = useState<Poytakirja | null>(null);
  const [f, setF] = useState<Lomake>({});
  const [vaara, setVaara] = useState(false);
  const [mittarit, setMittarit] = useState<Kalusto[]>([]);
  const [tila, setTila] = useState<string | null>(null);
  const [kerta, setKerta] = useState(0);
  useEffect(() => {
    void api.koepiirinPoytakirja(piiriId).then((r) => {
      const p = r as Poytakirja; setPk(p);
      const k = p.koe;
      setF(Object.fromEntries(LOMAKE_KENTAT.map((x) => {
        const v = k ? (k as Record<string, unknown>)[x] : null;
        if (v == null) return [x, x === "tulos" ? "kesken" : ""];
        if (x === "alkoi" || x === "paattyi") return [x, lomakkeelle(String(v))];
        return [x, String(v)];
      })));
      setVaara(k?.vaaravyohyke_kuitattu === true);
    });
  }, [piiriId, kerta]);
  useEffect(() => { void api.kalusto().then(setMittarit).catch(() => setMittarit([])); }, []);
  if (!pk) return <p className="dim">{t("kpk.ladataan")}</p>;
  const lukittu = pk.jaadytetty;
  const saaKirjata = onRooli(kayttaja, KIRJAAJAT) && !lukittu;
  const aseta = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));
  const tallenna = async () => {
    const b: Record<string, unknown> = {};
    for (const k of LOMAKE_KENTAT) {
      const v = f[k] ?? "";
      if (k === "alkoi" || k === "paattyi") b[k] = v ? lomakkeelta(v) : null;
      else if (NUMEROT.has(k)) b[k] = luku(v);
      else b[k] = v.trim() === "" ? null : v.trim();
    }
    if (!b.paiva) delete b.paiva;
    b.tulos = f.tulos || "kesken";
    b.vaaravyohyke_kuitattu = f.koetapa === "pneumaattinen" ? vaara : null;
    try { const r = await api.kirjaaPiirinKoe(piiriId, b); setTila(t("kpk.tallennettu", { n: r.rivit })); setKerta((x) => x + 1); muuttui(); }
    catch (e) { setTila(virhe(e)); }
  };
  const poista = async () => { try { await api.poistaKoepiiri(piiriId); poistettu(); } catch (e) { setTila(virhe(e)); } };
  const kentta = (k: string, tyyppi = "text") => (
    <label key={k} style={{ display: "inline-block", marginRight: 12 }}>{t(`kpk.${k}` as KaannosAvain)}<br />
      <input type={tyyppi} value={f[k] ?? ""} disabled={!saaKirjata} onChange={(e) => aseta(k, e.target.value)} data-koe-kentta={k} />
    </label>);
  return (
    <div className="panel" data-koepiiri-koe={pk.piiri.tunnus}>
      <h3>{t("kpk.piiri")} {pk.piiri.tunnus}{pk.piiri.korvaa_tunnus && <span className="dim"> · {t("kpk.korvaa")} {pk.piiri.korvaa_tunnus}</span>}</h3>
      <p>{pk.linjat.map((l) => l.tunnus).join(", ")}</p>
      {pk.havainnot.length > 0 && <p className="huom huom--varoitus">{pk.havainnot.join(" · ")}</p>}
      {lukittu && <p className="huom" data-koe-lukittu="">{t("kpk.lukittu")}</p>}
      <fieldset disabled={!saaKirjata} style={{ border: 0, padding: 0 }}>
        {kentta("paiva", "date")}
        <label style={{ display: "inline-block", marginRight: 12 }}>{t("kpk.tulos")}<br />
          <select value={f.tulos ?? "kesken"} onChange={(e) => aseta("tulos", e.target.value)} data-koe-kentta="tulos">
            {["kesken", "hyvaksytty", "hylatty", "ei_tiedossa"].map((x) => <option key={x} value={x}>{t(`kpk.tulos_${x}` as KaannosAvain)}</option>)}
          </select></label>
        <label style={{ display: "inline-block", marginRight: 12 }}>{t("kpk.koetapa")}<br />
          <select value={f.koetapa ?? ""} onChange={(e) => aseta("koetapa", e.target.value)} data-koe-kentta="koetapa">
            <option value="">–</option><option value="hydrostaattinen">{t("kpk.hydrostaattinen")}</option>
            <option value="pneumaattinen">{t("kpk.pneumaattinen")}</option></select></label>
        <label style={{ display: "inline-block", marginRight: 12 }}>{t("kpk.vaaliaine")}<br />
          <select value={f.vaaliaine ?? ""} onChange={(e) => aseta("vaaliaine", e.target.value)} data-koe-kentta="vaaliaine">
            <option value="">–</option>{["vesi", "typpi", "ilma", "muu"].map((x) => <option key={x} value={x}>{t(`kpk.${x}` as KaannosAvain)}</option>)}
          </select></label>
        <br />
        {kentta("koepaine_bar")}{kentta("koepaine_lahde")}{kentta("pitoaika_min")}{kentta("lampotila_c")}<br />
        {kentta("alkoi", "datetime-local")}{kentta("paattyi", "datetime-local")}{kentta("paine_alussa_bar")}{kentta("paine_lopussa_bar")}<br />
        <label style={{ display: "inline-block", marginRight: 12 }}>{t("kpk.mittalaite")}<br />
          <select value={f.mittalaite_id ?? ""} onChange={(e) => aseta("mittalaite_id", e.target.value)} data-koe-kentta="mittalaite_id">
            <option value="">–</option>{mittarit.map((m) => <option key={m.id} value={m.id}>{m.tunnus} {m.nimi}</option>)}</select></label>
        {kentta("kloridi_mg_l")}{kentta("valvoja")}{kentta("tarkastuslaitos")}{kentta("raportti")}<br />
        {f.koetapa === "pneumaattinen" && <>
          {kentta("riskinarvio_viite")}{kentta("suojaetaisyys_m")}
          <label><input type="checkbox" checked={vaara} onChange={(e) => setVaara(e.target.checked)} data-koe-kentta="vaaravyohyke_kuitattu" /> {t("kpk.vaaravyohyke_kuitattu")}</label><br />
        </>}
        {kentta("huomiot")}{kentta("ndt_ohitus_peruste")}
      </fieldset>
      {pk.koe?.ndt_tila_kirjattaessa && <p className="dim">{t("kpk.ndt")}: {pk.koe.ndt_tila_kirjattaessa}</p>}
      {pk.koe && pk.koe.kalibrointi_voimassa === false && <p className="huom huom--varoitus">{t("kpk.kal_ei_ui")}</p>}
      {tila && <p data-koe-tila="" role="status">{tila}</p>}
      {saaKirjata && <button className="nappi nappi--paa" onClick={() => void tallenna()} data-koe-tallenna="">{t("kpk.tallenna")}</button>}{" "}
      {saaKirjata && !pk.koe && <button className="nappi nappi--kevyt" onClick={() => void poista()} data-koepiiri-poista="">{t("kpk.poista")}</button>}
      <Allekirjoitukset pk={pk} kayttaja={kayttaja} muuttui={() => { setKerta((x) => x + 1); muuttui(); }} />
    </div>
  );
}

function Allekirjoitukset({ pk, kayttaja, muuttui }: { pk: Poytakirja; kayttaja: Kayttaja; muuttui: () => void }) {
  const [rooli, setRooli] = useState<string>("tilaaja");
  const [nimi, setNimi] = useState("");
  const [tiedosto, setTiedosto] = useState<File | null>(null);
  const [viesti, setViesti] = useState<string | null>(null);
  const saa = onRooli(kayttaja, KUITTAAJAT) && !pk.jaadytetty;
  const valmisKoe = pk.koe && (pk.koe.tulos === "hyvaksytty" || pk.koe.tulos === "hylatty");
  const allek = new Map(pk.allekirjoitukset.map((a) => [a.rooli, a]));
  const kuittaa = async () => { try { await api.allekirjoitaKoepiiri(pk.piiri.id, { rooli: "urakoitsija" }); muuttui(); } catch (e) { setViesti(virhe(e)); } };
  const skannaus = async () => {
    if (!tiedosto) return;
    const sisalto = await new Promise<string>((ok, ei) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = ei; r.readAsDataURL(tiedosto); });
    try { await api.allekirjoitaKoepiiri(pk.piiri.id, { rooli, nimi, liite: { nimi: tiedosto.name, sisalto } }); setNimi(""); setTiedosto(null); muuttui(); }
    catch (e) { setViesti(virhe(e)); }
  };
  const poista = async (r: string) => { try { await api.poistaKoepiirinAllekirjoitus(pk.piiri.id, r); muuttui(); } catch (e) { setViesti(virhe(e)); } };
  const lukitse = async () => { try { await api.lukitseKoepiirinPoytakirja(pk.piiri.id); muuttui(); } catch (e) { setViesti(virhe(e)); } };
  // Vaaditut koossa, valinnaiset (tilaaja, Toni 4.11. 08.50) saavat puuttua: lukitus on oma tekonsa.
  const lukittavissa = saa && pk.tila.tila === "odottaa_allekirjoitusta" && pk.tila.puuttuvat.length === 0;
  return (
    <div data-allekirjoitukset={pk.tila.tila}>
      <h4>{t("kpk.allekirjoitukset")}: <span data-poytakirja-tila={pk.tila.tila}>{tilaTeksti(pk, t)}</span></h4>
      <ul>
        {ROOLIT.map((r) => {
          const a = allek.get(r);
          const vaaditaan = pk.vaaditut.includes(r);
          return (
            <li key={r} data-allekirjoitus-rooli={r} data-allekirjoitettu={a ? "1" : "0"}>
              {t(`kpk.rooli_${r}` as KaannosAvain)}{!vaaditaan && <span className="dim"> ({t(r === "tilaaja" ? "kpk.valinnainen" : "kpk.ei_vaadita_lyhyt")})</span>}:{" "}
              {a ? <>{a.nimi} · {t(a.tapa === "sahkoinen" ? "kpk.sahkoinen" : "kpk.paperi", { hetki: helsinginAika(a.hetki, kayttoliittymanKieli() === "en" ? "en" : "fi", t("aika.klo")) ?? "" })}
                    {saa && <> <button className="nappi nappi--kevyt" onClick={() => void poista(r)}>{t("kpk.poista")}</button></>}</>
                : <span className="dim">{t("kpk.puuttuu")}</span>}
            </li>);
        })}
      </ul>
      {saa && !valmisKoe && <p className="dim">{t("kpk.allekirjoitus_kun_valmis")}</p>}
      {lukittavissa && <p><button className="nappi nappi--paa" onClick={() => void lukitse()} data-lukitse-poytakirja="">{t("kpk.lukitse")}</button>{" "}
        <span className="dim">{t("kpk.lukitse_ohje")}</span></p>}
      {saa && valmisKoe && !allek.has("urakoitsija") &&
        <p><button className="nappi" onClick={() => void kuittaa()} data-kuittaa-urakoitsija="">{t("kpk.kuittaa")}</button></p>}
      {saa && valmisKoe && (
        <p>
          <select value={rooli} onChange={(e) => setRooli(e.target.value)} data-skannaus-rooli="">
            <option value="tilaaja">{t("kpk.rooli_tilaaja")}</option><option value="tarkastuslaitos">{t("kpk.rooli_tarkastuslaitos")}</option>
          </select>{" "}
          <input placeholder={t("kpk.allekirjoittaja")} value={nimi} onChange={(e) => setNimi(e.target.value)} data-skannaus-nimi="" />{" "}
          <input type="file" accept="application/pdf,image/*" onChange={(e) => setTiedosto(e.target.files?.[0] ?? null)} data-skannaus-tiedosto="" />{" "}
          <button className="nappi" disabled={!tiedosto || !nimi.trim()} onClick={() => void skannaus()} data-skannaus-lisaa="">{t("kpk.lisaa_skannaus")}</button>
        </p>)}
      {viesti && <p className="huom huom--vika" role="alert">{viesti}</p>}
    </div>
  );
}

function tilaTeksti(pk: Poytakirja, tx: (a: KaannosAvain, m?: Record<string, string | number>) => string): string {
  const s = pk.tila.tila;
  if (s === "allekirjoitettu") return tx("kpk.tila_valmis");
  if (s === "ei_asiakirjaa" && !pk.koe) return tx("kpk.tila_ei");
  if (s === "odottaa_allekirjoitusta" && pk.tila.puuttuvat.length === 0) return tx("kpk.tila_lukittavissa");
  const kuka = pk.tila.puuttuvat.map((r) => tx(`kpk.rooli_${r}` as KaannosAvain)).join(", ");
  return tx("kpk.tila_odottaa", { kuka: kuka || "–" });
}

/** Tulosteen teksti valitulla kielellä; puuttuva käännös → suomi (tuloste ei näytä avainta). */
function tulosteT(kieli: "fi" | "en") {
  return (a: KaannosAvain, m?: Record<string, string | number>) => {
    let s = kaannos(a, kieli) ?? kaannos(a, "fi") ?? a;
    if (m) for (const [k, v] of Object.entries(m)) s = s.split(`{${k}}`).join(String(v));
    return s;
  };
}

function Poytakirjatuloste({ piiriId, sulje }: { piiriId: number; sulje: () => void }) {
  const [valinta, setValinta] = useState("");
  const [pk, setPk] = useState<Poytakirja | null>(null);
  const [vika, setVika] = useState<string | null>(null);
  useEffect(() => { setPk(null); api.koepiirinPoytakirja(piiriId, valinta || undefined).then((r) => setPk(r as Poytakirja)).catch((e) => setVika(virhe(e))); }, [piiriId, valinta]);
  // Selaimen «Tallenna PDF:nä» ehdottaa sivun otsikkoa: ensimmäisen linjan nimi nimeämissäännöstä (luku 3.4). Hook ennen aikaista paluuta (#310).
  useEffect(() => {
    const nimi = pk?.tiedostonimet[0]; if (!nimi) return;
    const vanha = document.title; document.title = nimi.replace(/\.pdf$/, ""); return () => { document.title = vanha; };
  }, [pk]);
  if (vika) return <p className="huom huom--vika" role="alert">{vika}</p>;
  if (!pk) return null;
  const kieli = pk.kieli.kieli;
  const T = tulosteT(kieli);
  const ei = T("kpk.ei");
  // Luku tulosteen kielen desimaalierottimella (fi pilkku, en piste); muu arvo sellaisenaan. Puuttuva on sana, ei tyhjä.
  const lukuTeksti = (v: unknown) => (typeof v === "number" ? v.toLocaleString(kieli === "en" ? "en-GB" : "fi-FI", { maximumFractionDigits: 3 }) : String(v));
  const arvo = (v: unknown, yks = "") => (v == null || v === "" ? <span className="dim">{ei}</span> : <>{lukuTeksti(v)}{yks}</>);
  const pv = (v: string | null) => (v ? new Date(v.length === 10 ? v + "T12:00:00" : v).toLocaleDateString(kieli === "en" ? "en-GB" : "fi-FI") : null);
  const aika = (v: string | null) => helsinginAika(v, kieli, T("aika.klo"));
  const k = pk.koe;
  const muutos = k && k.paine_alussa_bar != null && k.paine_lopussa_bar != null ? Math.round((k.paine_lopussa_bar - k.paine_alussa_bar) * 1000) / 1000 : null;
  const allek = new Map(pk.allekirjoitukset.map((a) => [a.rooli, a]));
  const laitosTila = pk.piiri.tarkastuslaitos_vaaditaan === true ? T("kpk.vaaditaan", { luokka: pk.piiri.ped_luokka ?? "?" })
    : pk.piiri.tarkastuslaitos_vaaditaan === false ? T("kpk.ei_vaadita", { luokka: pk.piiri.ped_luokka ?? "?" }) : T("kpk.ped_avoin");
  const rivi = (otsikko: KaannosAvain, sisalto: ReactNode) => <tr><th style={{ textAlign: "left", width: "38%" }}>{T(otsikko)}</th><td>{sisalto}</td></tr>;
  return (
    <TulostePohja otsikko={T("kpk.otsikko")} sulje={sulje} tunniste="painekoepoytakirja"
      merkinta={pk.jaadytetty ? undefined : tilaTeksti(pk, T)}
      meta={<span data-tuloste-kieli={kieli} className="kpk-tulostekieli">
        {/* Valitsin on näytön työkalu, ei pöytäkirjan sisältöä (Toni 4.11.2026 klo 10.10 kohta 3): ei paperille. */}
        <style>{"@media print { .kpk-tulostekieli { display: none !important; } }"}</style>
        <TulosteKieli arvo={valinta} muuta={setValinta} /></span>}>
      <table className="taulu" data-poytakirja-tunnistus="">
        <tbody>
          {rivi("kpk.kohde", <>{pk.piiri.kohde_tunnus} {pk.piiri.kohde_nimi}</>)}
          {rivi("kpk.piiri", <>{pk.piiri.tunnus}{pk.piiri.kuvaus ? ` · ${pk.piiri.kuvaus}` : ""}</>)}
          {pk.piiri.korvaa_tunnus && rivi("kpk.korvaa", pk.piiri.korvaa_tunnus)}
          {rivi("kpk.raportti", arvo(k?.raportti))}
          {rivi("kpk.ped", pk.piiri.ped_luokka ?? T("kpk.ped_avoin"))}
        </tbody>
      </table>
      <h3>{T("kpk.linjat")}</h3>
      <table className="taulu" data-poytakirja-linjat={pk.linjat.length}>
        <thead><tr><th>{T("kpk.linja")}</th><th>DN</th><th>{T("kpk.ped")}</th><th>{T("kpk.media")}</th><th>{T("kpk.putkiluokka")}</th>
          <th>{T("kpk.ps")}</th><th>{T("kpk.ts")}</th><th>{T("kpk.kemikaali")}</th></tr></thead>
        <tbody>{pk.linjat.map((l) => (
          <tr key={l.id}><td>{l.tunnus}</td><td>{arvo(l.dn_nimellinen)}</td><td>{arvo(l.ped_luokka)}</td><td>{arvo(l.media)}</td><td>{arvo(l.putkiluokka)}</td>
            <td>{arvo(l.suunnittelupaine_bar)}</td><td>{arvo(l.suunnittelulampotila_c)}</td>
            <td>{l.kemikaaliputkisto == null ? <span className="dim">{ei}</span> : l.kemikaaliputkisto ? T("kpk.kylla") : T("kpk.ei_arvo")}</td></tr>))}</tbody>
      </table>
      <h3>{T("kpk.koe")}</h3>
      <table className="taulu" data-poytakirja-koe="">
        <tbody>
          {rivi("kpk.paiva", arvo(pv(k?.paiva ?? null)))}
          {rivi("kpk.koetapa", k?.koetapa ? T(`kpk.${k.koetapa}` as KaannosAvain) : arvo(null))}
          {rivi("kpk.vaaliaine", k?.vaaliaine ? T(`kpk.${k.vaaliaine}` as KaannosAvain) : arvo(null))}
          {rivi("kpk.koepaine_bar", arvo(k?.koepaine_bar))}
          {rivi("kpk.koepaine_lahde", arvo(k?.koepaine_lahde))}
          {rivi("kpk.pitoaika_min", arvo(k?.pitoaika_min))}
          {rivi("kpk.alkoi", arvo(aika(k?.alkoi ?? null)))}
          {rivi("kpk.paattyi", arvo(aika(k?.paattyi ?? null)))}
          {rivi("kpk.lampotila_c", arvo(k?.lampotila_c))}
          {rivi("kpk.paine_alussa_bar", arvo(k?.paine_alussa_bar))}
          {rivi("kpk.paine_lopussa_bar", arvo(k?.paine_lopussa_bar))}
          {rivi("kpk.muutos", arvo(muutos))}
          {rivi("kpk.kloridi_mg_l", arvo(k?.kloridi_mg_l))}
          {k?.koetapa === "pneumaattinen" && <>
            {rivi("kpk.riskinarvio_viite", arvo(k.riskinarvio_viite))}
            {rivi("kpk.suojaetaisyys_m", arvo(k.suojaetaisyys_m))}
            {rivi("kpk.vaaravyohyke_kuitattu", k.vaaravyohyke_kuitattu ? T("kpk.kylla") : T("kpk.ei_arvo"))}</>}
          {rivi("kpk.mittalaite", <>{arvo(k?.mittalaite)}{k?.mittalaite_sarjanumero ? ` · ${k.mittalaite_sarjanumero}` : ""}</>)}
          {rivi("kpk.kalibrointi", k?.mittalaite_id == null ? arvo(null)
            : <>{arvo(pv(k.kalibrointi_voimassa_asti))} · {k.kalibrointi_voimassa ? T("kpk.kal_ok") : <strong>{T("kpk.kal_ei")}</strong>}</>)}
          {rivi("kpk.ndt", arvo(k?.ndt_tila_kirjattaessa))}
          {k?.ndt_ohitus_peruste && rivi("kpk.ohitus", k.ndt_ohitus_peruste)}
          {rivi("kpk.tulos", <strong data-poytakirja-tulos={k?.tulos ?? "ei"}>{k?.tulos ? T(`kpk.tulos_${k.tulos}` as KaannosAvain) : arvo(null)}</strong>)}
          {rivi("kpk.huomiot", arvo(k?.huomiot))}
        </tbody>
      </table>
      <h3>{T("kpk.allekirjoitukset")}</h3>
      <table className="taulu" data-poytakirja-allekirjoitukset="">
        <thead><tr><th /><th>{T("kpk.nimi")}</th><th>{T("kpk.pvm")}</th><th>{T("kpk.allekirjoitus")}</th></tr></thead>
        <tbody>{ROOLIT.map((r) => {
          const a = allek.get(r);
          const nimiArvo = a?.nimi ?? (r === "tilaaja" ? k?.valvoja : r === "tarkastuslaitos" ? k?.tarkastuslaitos : null);
          return (
            <tr key={r} data-tuloste-rooli={r} style={{ height: 48 }}>
              <th style={{ textAlign: "left" }}>{T(`kpk.rooli_${r}` as KaannosAvain)}{r === "tarkastuslaitos" && <div className="dim">{laitosTila}</div>}</th>
              <td>{nimiArvo ?? ""}</td><td>{a ? pv(a.hetki) : ""}</td>
              <td>{a ? (a.tapa === "sahkoinen" ? T("kpk.sahkoinen", { hetki: aika(a.hetki) ?? "" }) : T("kpk.paperi", { hetki: aika(a.hetki) ?? "" })) : ""}</td>
            </tr>);
        })}</tbody>
      </table>
      <p className="dim">{T("kpk.tiedostonimet")}: {pk.tiedostonimet.length ? pk.tiedostonimet.join(", ") : ei}</p>
    </TulostePohja>
  );
}
